import { SolButton } from "@solstice/sol-react";
import { PUBLISH_STATUS_PUBLISHED } from "constant";
import { FC, useState } from "react";
import { ProfileProps } from "types";
import Modal from "ui-molecules/Modal/Modal";

type PublishModalProfileSelectionProps = {
  profiles: ProfileProps[];
  onSelectProfiles: (profiles: ProfileProps[]) => void;
  onCancel: () => void;
};

export const PublishModalProfileSelection: FC<PublishModalProfileSelectionProps> = ({
  profiles,
  onSelectProfiles,
  onCancel,
}) => {
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileProps[]>([]);

  const sortedProfiles = [...profiles].sort((a, b) => {
    const countryComparison = a.country.name.localeCompare(b.country.name);
    return countryComparison !== 0
      ? countryComparison
      : a.language_name.localeCompare(b.language_name);
  });

  const allSelected =
    sortedProfiles.length > 0 &&
    selectedProfiles.length === sortedProfiles.length;

  const onAccept = () => {
    onSelectProfiles(selectedProfiles);
  };

  const toggleProfileSelection = (profile: ProfileProps) => {
    setSelectedProfiles((prev) => {
      const isSelected = prev.some((p) => p.id === profile.id);
      if (isSelected) {
        return prev.filter((p) => p.id !== profile.id);
      } else {
        return [...prev, profile];
      }
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles([...sortedProfiles]);
    }
  };

  return (
    <>
      <Modal.Body>
        <div className="w-full">
          <div className="mb-4">
            <div className="flex flex-col gap-3 mb-3">
              <h3 className="text-lg font-medium text-gray-800">
                Choose the country profiles where you'd like to share your
                updates. These changes will be shared to JLL.com for the
                profiles you select below.
              </h3>
              <div className="self-start">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                >
                  {allSelected ? "Unselect All" : "Select All"}
                </button>
              </div>
            </div>

            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-2">
              {sortedProfiles.map((profile) => {
                const isPublished =
                  profile.publish_status === PUBLISH_STATUS_PUBLISHED;
                const isSelected = selectedProfiles.some(
                  (p) => p.id === profile.id
                );

                return (
                  <div
                    key={profile.id}
                    className="flex items-center p-2 rounded hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      id={`profile-${profile.id}`}
                      checked={isSelected}
                      onChange={() => toggleProfileSelection(profile)}
                      className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <label
                      htmlFor={`profile-${profile.id}`}
                      className="ml-2.5 text-gray-700 cursor-pointer flex-grow"
                    >
                      {profile.country.name} - {profile.language_name}
                    </label>

                    <div className="flex items-center ml-2">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          isPublished ? "bg-green-500" : "bg-gray-400"
                        }`}
                      ></div>
                      <span
                        className={`ml-1.5 text-xs ${
                          isPublished ? "text-green-600" : "text-gray-500"
                        }`}
                      >
                        {isPublished ? "Published" : "Unpublished"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex flex-row space-x-3">
          <SolButton
            variant="secondary"
            className="!min-w-[96px] sol-w-full"
            onSol-click={onCancel}
          >
            Cancel
          </SolButton>
          <SolButton
            className="min-w-[96px] sol-w-full whitespace-nowrap"
            onSol-click={onAccept}
            disabled={selectedProfiles.length === 0}
          >
            Continue
          </SolButton>
        </div>
      </Modal.Footer>
    </>
  );
};
